/**
 * Step 2: Portal Configuration
 * Agent sets up their branded portal
 */

import React, { useState, useEffect } from 'react';
import { Form, Button, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { SignupData } from '../AgentSignup';
import agentPortalService from '../../../services/agentPortal.service';

interface StepTwoProps {
  data: SignupData;
  updateData: (data: Partial<SignupData>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const StepTwo: React.FC<StepTwoProps> = ({ data, updateData, onNext, onPrevious }) => {
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [subdomainDebounceTimer, setSubdomainDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  
  // Clean subdomain input (lowercase, alphanumeric, hyphens only)
  const cleanSubdomain = (value: string) => {
    return value.toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
      .replace(/^-+/, '')
      .replace(/-+$/, '')
      .replace(/--+/g, '-');
  };
  
  // Check subdomain availability
  const checkSubdomainAvailability = async (subdomain: string) => {
    if (subdomain.length < 3) {
      setSubdomainAvailable(null);
      return;
    }
    
    setCheckingAvailability(true);
    
    try {
      const result = await agentPortalService.checkSubdomainAvailability(subdomain);
      setSubdomainAvailable(result.available);
    } catch (error) {
      console.error('Error checking subdomain:', error);
      setSubdomainAvailable(false);
    } finally {
      setCheckingAvailability(false);
    }
  };
  
  // Debounce subdomain checking
  useEffect(() => {
    if (data.desiredSubdomain && data.desiredSubdomain.length >= 3) {
      if (subdomainDebounceTimer) {
        clearTimeout(subdomainDebounceTimer);
      }
      
      const timer = setTimeout(() => {
        checkSubdomainAvailability(data.desiredSubdomain);
      }, 500);
      
      setSubdomainDebounceTimer(timer);
    } else {
      setSubdomainAvailable(null);
    }
    
    return () => {
      if (subdomainDebounceTimer) {
        clearTimeout(subdomainDebounceTimer);
      }
    };
  }, [data.desiredSubdomain]);
  
  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!data.desiredSubdomain.trim()) {
      newErrors.desiredSubdomain = 'Portal name is required';
    } else if (data.desiredSubdomain.length < 3) {
      newErrors.desiredSubdomain = 'Portal name must be at least 3 characters';
    } else if (subdomainAvailable === false) {
      newErrors.desiredSubdomain = 'This portal name is already taken';
    }
    
    if (!data.brokerageName.trim()) {
      newErrors.brokerageName = 'Brokerage name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };
  
  const handleSubdomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = cleanSubdomain(e.target.value);
    updateData({ desiredSubdomain: cleaned });
    
    // Clear error when user starts typing
    if (errors.desiredSubdomain) {
      setErrors({ ...errors, desiredSubdomain: '' });
    }
  };
  
  const handleChange = (field: keyof SignupData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateData({ [field]: e.target.value });
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // Generate subdomain suggestion based on name
  const suggestSubdomain = () => {
    if (data.firstName && data.lastName) {
      const suggestion = cleanSubdomain(
        `${data.firstName}-${data.lastName}`.toLowerCase()
      );
      updateData({ desiredSubdomain: suggestion });
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="signup-form">
      <div className="step-header">
        <h2>Set Up Your Portal</h2>
        <p>Choose your portal address and business info</p>
      </div>

      <Form.Group className="mb-4">
        <Form.Label>Your Portal Address</Form.Label>
        <InputGroup>
          <Form.Control
            type="text"
            value={data.desiredSubdomain}
            onChange={handleSubdomainChange}
            placeholder="yourname"
            isInvalid={!!errors.desiredSubdomain}
            className="subdomain-input"
            autoFocus
          />
          <div className="subdomain-suffix">.aisearchportal.com</div>
        </InputGroup>
        <div className="form-helper">
          This will be your unique web address
          {data.firstName && data.lastName && !data.desiredSubdomain && (
            <Button
              variant="link"
              size="sm"
              className="ms-2 p-0"
              onClick={suggestSubdomain}
            >
              Use {cleanSubdomain(`${data.firstName}-${data.lastName}`)}?
            </Button>
          )}
        </div>
        
        {/* Availability check */}
        {data.desiredSubdomain && data.desiredSubdomain.length >= 3 && (
          <div className="availability-check">
            {checkingAvailability && (
              <span className="text-muted">
                <Spinner size="sm" className="me-2" />
                Checking availability...
              </span>
            )}
            {!checkingAvailability && subdomainAvailable === true && (
              <span className="available">
                <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                Available!
              </span>
            )}
            {!checkingAvailability && subdomainAvailable === false && (
              <span className="unavailable">
                <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                Already taken - try another name
              </span>
            )}
          </div>
        )}
        
        {errors.desiredSubdomain && (
          <div className="form-error">{errors.desiredSubdomain}</div>
        )}
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Brokerage Name</Form.Label>
        <Form.Control
          type="text"
          value={data.brokerageName}
          onChange={handleChange('brokerageName')}
          placeholder="ABC Realty"
          isInvalid={!!errors.brokerageName}
        />
        <div className="form-helper">
          This will appear on your portal
        </div>
        {errors.brokerageName && (
          <div className="form-error">{errors.brokerageName}</div>
        )}
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>MLS ID</Form.Label>
        <Form.Control
          type="text"
          value={data.mlsId || ''}
          onChange={handleChange('mlsId')}
          placeholder="e.g. ARMLS-12345"
        />
        <div className="form-helper">
          Your MLS member ID for property data access
        </div>
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>License Number (Optional)</Form.Label>
        <Form.Control
          type="text"
          value={data.licenseNumber || ''}
          onChange={handleChange('licenseNumber')}
          placeholder="e.g. SA123456789"
        />
        <div className="form-helper">
          Your real estate license number for compliance
        </div>
      </Form.Group>

      {/* Preview Card */}
      <div className="portal-preview mt-5">
        <h5 className="mb-3">Your Portal Preview:</h5>
        <div className="preview-card">
          <div className="preview-url">
            <span className="text-muted">https://</span>
            <span className="fw-bold">
              {data.desiredSubdomain || 'yourname'}
            </span>
            <span className="text-muted">.aisearchportal.com</span>
          </div>
          <div className="preview-business">
            {data.brokerageName || 'Your Brokerage Name'}
          </div>
          <div className="preview-agent">
            {data.firstName} {data.lastName}
            {data.mlsId && <span className="text-muted ms-2">• MLS: {data.mlsId}</span>}
            {data.licenseNumber && <span className="text-muted ms-2">• Lic: {data.licenseNumber}</span>}
          </div>
        </div>
      </div>

      {/* Terms & Conditions */}
      <div style={{ marginBottom: '20px', padding: '14px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '12px', color: '#555', lineHeight: 1.6 }}>
          <input
            type="checkbox"
            checked={data.agreedToTerms || false}
            onChange={(e) => updateData({ agreedToTerms: e.target.checked })}
            style={{ width: '16px', height: '16px', marginTop: '2px', cursor: 'pointer', flexShrink: 0 }}
          />
          <span>
            I agree to the{' '}
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: '#C9952A', fontWeight: 600 }}>
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#C9952A', fontWeight: 600 }}>
              Privacy Policy
            </a>
            . I understand that my data will be processed in accordance with KairosIQ's privacy practices.
          </span>
        </label>
      </div>

      <div className="step-buttons">
        <Button
          type="button"
          variant="outline-secondary"
          className="btn-previous"
          onClick={onPrevious}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back
        </Button>
        <Button
          type="submit"
          className="btn-next"
          disabled={checkingAvailability || subdomainAvailable === false || !data.agreedToTerms}
        >
          Continue
          <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
        </Button>
      </div>
    </Form>
  );
};

export default StepTwo;